/**
 * React Query Hook: Get Retrieval Metadata Items
 * 
 * Fetches files (metadata items) for a specific metadata type
 * Supports pagination with cursor-based navigation
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { GET_RETRIEVAL_ITEMS_QUERY } from '@/services/metadata_v1/queries';
import type {
  GetRetrievalItemsResponse,
  GetRetrievalItemsVariables,
  RetrievalMetadataItemsResponse,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Fetch retrieval metadata items from API (exported for use in loadChildren callbacks)
 */
export async function fetchRetrievalMetadataItems(
  variables: GetRetrievalItemsVariables
): Promise<RetrievalMetadataItemsResponse> {
  const response = await graphqlRequest<GetRetrievalItemsResponse>(
    GET_RETRIEVAL_ITEMS_QUERY,
    variables
  );
  return response.retrievalMetadataItems;
}

/**
 * Hook to fetch retrieval metadata items (files) for a specific metadata type
 * 
 * Automatically uses retrieval ID from selected organization
 * 
 * @param metadataType - The metadata type to fetch files for (e.g., "ApexClass")
 * @param options - Query options including pagination
 * @returns React Query result with paginated metadata items
 */
export function useRetrievalMetadataItems(
  metadataType: string,
  options?: {
    enabled?: boolean;
    first?: number;
    after?: string | null;
  }
): UseQueryResult<RetrievalMetadataItemsResponse, Error> {
  const { enabled = true, first = 20, after } = options || {};
  
  // Subscribe to selected organization from auth store (this ensures re-render when store updates)
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  
  // Extract retrievalId
  const retrievalId = selectedOrg?.latestMetadataRetrieval?.id;
  

  return useQuery({
    queryKey: ['retrievalMetadataItems', retrievalId, metadataType, first, after],
    queryFn: () =>
      fetchRetrievalMetadataItems({
        retrievalId: retrievalId!,
        metadataType,
        first,
        after,
      }),
    enabled: enabled && !!retrievalId && !!metadataType,
    staleTime: 5 * 60 * 1000, // 5 minutes - file lists are relatively stable
    refetchOnWindowFocus: false,
  });
}